import { useState, useEffect } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import SharedService from "@/services/shared-service";

/**
 * Event Hero Slider Component
 * Displays events in a carousel format with red/orange gradient background
 * 
 * @param {Array} events - Array of event objects from DocumentsSlider data
 * @param {number} autoPlayInterval - Auto-play interval in milliseconds (default: 5000)
 */
export default function EventHeroSlider({ events = [], autoPlayInterval = 5000 }) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (!isHovered && events.length > 0) {
            const timer = setInterval(() => {
                handleNextSlide();
            }, autoPlayInterval);
            return () => clearInterval(timer);
        }
    }, [isHovered, currentSlide, events.length]);

    const handleNextSlide = () => {
        setCurrentSlide((prev) => (prev === events.length - 1 ? 0 : prev + 1));
    };

    const handlePrevSlide = () => {
        setCurrentSlide((prev) => (prev === 0 ? events.length - 1 : prev - 1));
    };

    if (!events || events.length === 0) {
        return (
            <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-[5px] shadow-md p-8 text-white text-center">
                <p>Không có sự kiện nào</p>
            </div>
        );
    }

    const currentEvent = events[currentSlide];

    return (
        <div
            className="relative bg-gradient-to-r from-red-600 via-red-500 to-orange-400 rounded-[5px] shadow-md overflow-hidden h-full"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Background Image Overlay */}
            {currentEvent?.photo && (
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ 
                        backgroundImage: `url(${SharedService.getOptimizeImage(currentEvent.photo, 'hd')})` 
                    }}
                />
            )}

            {/* Navigation Arrows */}
            <button
                onClick={handlePrevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all"
                aria-label="Previous event"
            >
                <BiChevronLeft className="w-8 h-8 text-white" />
            </button>

            <button
                onClick={handleNextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all"
                aria-label="Next event"
            >
                <BiChevronRight className="w-8 h-8 text-white" />
            </button>

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-center items-center h-full px-8 py-12 text-white text-center">
                {/* Category Badge */}
                {currentEvent?.categoryName && (
                    <div className="bg-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                        {currentEvent.categoryName}
                    </div>
                )}

                {/* Author/Organization */}
                {currentEvent?.authorName && (
                    <p className="text-lg font-medium mb-4 italic">
                        {currentEvent.authorName}
                    </p>
                )}

                {/* Main Title */}
                <a 
                    href={SharedService.getPostPath(currentEvent)}
                    className="block hover:opacity-90 transition-opacity"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight max-w-4xl drop-shadow-lg">
                        {currentEvent?.quotation || currentEvent?.title}
                    </h2>

                    {/* Description */}
                    {currentEvent?.metaDescription && (
                        <div 
                            className="text-lg md:text-xl mb-6 max-w-3xl mx-auto line-clamp-3 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: currentEvent.metaDescription }}
                        />
                    )}
                </a>

                {/* Date/Time Badge */}
                {currentEvent?.publishedDate && (
                    <div className="bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                        <span>📅</span>
                        <span className="uppercase">
                            {SharedService.formatISODate(currentEvent.publishedDate, "DD.MM.YYYY")}
                        </span>
                    </div>
                )}

                {/* Location */}
                {currentEvent?.quotation && currentEvent?.title && (
                    <p className="text-base mt-4 opacity-90">
                        📍 {currentEvent.title}
                    </p>
                )}
            </div>

            {/* Dots Indicator */}
            {events.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {events.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                                index === currentSlide
                                    ? "bg-white w-8"
                                    : "bg-white/50 hover:bg-white/75"
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
